import { useEffect, useMemo, useState } from 'react';
import {
  FolderTree, ArrowDown, Layers, Tag, ExternalLink, AlertCircle, BarChart3,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import { api } from '../api/feedy';

// The field the category master-rule group writes to. Each matching rule sets
// google_category to a taxonomy PATH; the google_shopping channel later converts
// that path to Google's numeric taxonomy ID on output.
const CATEGORY_FIELD = 'google_category';

// Actions can be a single object or a list (a rule may set several fields).
const actionsOf = (rule) => (Array.isArray(rule.action) ? rule.action : [rule.action || {}]);

// The action within a rule that sets the category path (if any).
const categoryAction = (rule) =>
  actionsOf(rule).find((a) => a && a.type === 'set' && a.field === CATEGORY_FIELD);

const setsCategory = (rule) => !!categoryAction(rule);

// Count how many of a group's rules set the category field — used to pick the
// right group when several exist (or none clearly qualifies).
const categoryRuleCount = (group) => (group.rules || []).filter(setsCategory).length;

// Flatten a (possibly nested AND/OR) condition tree into its leaf conditions so
// we can surface the collection name(s) each rule matches on.
const flattenLeaves = (condition) => {
  if (!condition) return [];
  if (Array.isArray(condition.conditions)) {
    return condition.conditions.flatMap(flattenLeaves);
  }
  return [condition];
};

// The collection value(s) a category rule matches on. We read them straight from
// the live rule rather than hardcoding a field name, so the page stays correct
// whatever the collections field is actually called.
const matchedCollections = (rule) =>
  flattenLeaves(rule.condition)
    .map((c) => c && c.value)
    .filter((v) => v != null && v !== '');

// The taxonomy path this rule assigns, e.g.
// "Home & Garden > Lighting > Lighting Fixtures > Ceiling Light Fixtures".
const categoryPath = (rule) => (categoryAction(rule) || {}).value || '';

// Thousands-separated integer, e.g. 15783 → "15,783".
const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');

// Round a share to a sensible % string (keeps one decimal under 10% so small
// categories don't all collapse to "0%").
const fmtPct = (count, total) => {
  if (!total) return '0%';
  const p = (count / total) * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${p < 10 ? p.toFixed(1) : Math.round(p)}%`;
};

// Render a taxonomy path as breadcrumb segments, dimming all but the leaf.
function CategoryPath({ path }) {
  const segs = String(path).split('>').map((s) => s.trim()).filter(Boolean);
  if (segs.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="font-mono text-[12px] leading-relaxed">
      {segs.map((seg, i) => (
        <span key={i}>
          <span className={i === segs.length - 1 ? 'font-medium text-foreground' : 'text-muted-foreground'}>{seg}</span>
          {i < segs.length - 1 && <span className="px-1 text-muted-foreground/60">›</span>}
        </span>
      ))}
    </span>
  );
}

// One step in the plain-language explainer.
function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/12 font-mono text-[11px] font-semibold text-primary tnum">{n}</span>
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-[13px] leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

export default function Categorisation() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Final-volume distribution (count of products that END UP in each rule's
  // category after the whole cascade). Loaded separately — it's a ~43k scan.
  const [dist, setDist] = useState(null);
  const [distLoading, setDistLoading] = useState(false);

  useEffect(() => {
    api.masterRuleGroups.list()
      .then((g) => setGroups(g || []))
      .catch(() => setError('Could not load master rule groups.'))
      .finally(() => setLoading(false));
  }, []);

  // Pick the group that drives categorisation: the one with the most rules that
  // set google_category. If several qualify equally we keep the first.
  const group = useMemo(() => {
    const qualifying = groups
      .filter((g) => categoryRuleCount(g) > 0)
      .sort((a, b) => categoryRuleCount(b) - categoryRuleCount(a));
    return qualifying[0] || null;
  }, [groups]);

  // The category-setting rules, in execution order (top → bottom). Later rules
  // overwrite earlier ones, so the LAST match wins.
  const rules = useMemo(() => {
    if (!group) return [];
    return (group.rules || [])
      .filter(setsCategory)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [group]);

  // Once we know which group drives categorisation, fetch how many products
  // actually end up in each category (the final cascade volume).
  useEffect(() => {
    if (!group) return undefined;
    let cancelled = false;
    setDistLoading(true);
    setDist(null);
    api.masterRuleGroups.distribution(group.id)
      .then((d) => { if (!cancelled) setDist(d); })
      .catch(() => { if (!cancelled) setDist(null); })
      .finally(() => { if (!cancelled) setDistLoading(false); });
    return () => { cancelled = true; };
  }, [group]);

  const total = dist?.total || 0;
  // Count of products that end up assigned to a given rule's category.
  const ruleCount = (rule) => (dist?.per_rule ? dist.per_rule[rule.id] : undefined);

  return (
    <AppLayout
      title="Categorisation"
      description="How each product's Google product category is decided — from Shopify collections to a taxonomy ID."
    >
      <div className="mx-auto max-w-5xl space-y-6">
        {/* ---- plain-language explainer ---------------------------------- */}
        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <FolderTree className="h-[18px] w-[18px] text-primary" />
            <h2 className="text-[15px] font-semibold tracking-tight">How a category is chosen</h2>
          </div>
          <div className="space-y-4">
            <Step n={1} title="Every product needs a Google product category">
              Google Shopping requires a <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">google_product_category</code> from
              Google&apos;s fixed product taxonomy. Feedy derives one for every product automatically.
            </Step>
            <Step n={2} title="It&apos;s derived from the product&apos;s Shopify collections">
              A dedicated set of <span className="font-medium text-foreground">master rules</span> looks at which Shopify
              collections a product belongs to. Each rule says: <span className="italic">&ldquo;if the product is in a collection
              matching X, set the category to Y.&rdquo;</span>
            </Step>
            <Step n={3} title="Rules run top to bottom — the last match wins">
              Rules are evaluated in order and a later match <span className="font-medium text-foreground">overwrites</span> an
              earlier one. So ordering matters: broad / generic categories sit first, specific ones last. A fixture in both a
              <span className="font-medium text-foreground"> Wall</span> and a <span className="font-medium text-foreground">Ceiling</span> collection
              ends up <span className="font-medium text-foreground">Ceiling</span>, because the Ceiling rule runs after the Wall rule.
            </Step>
            <Step n={4} title="The chosen path is converted to a numeric taxonomy ID">
              The category <span className="font-medium text-foreground">path</span> (e.g.
              <span className="font-mono text-[12px]"> Home &amp; Garden &gt; Lighting &gt; Lighting Fixtures &gt; Ceiling Light Fixtures</span>) is
              translated to Google&apos;s numeric taxonomy ID when the feed is built.
            </Step>
          </div>

          {/* the "last match wins" mental model, drawn out */}
          <div className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-[12px]">
            <Badge variant="outline" className="font-mono">Wall · #2</Badge>
            <ArrowDown className="h-3.5 w-3.5 rotate-[-90deg] text-muted-foreground" />
            <Badge variant="outline" className="font-mono">Ceiling · #7</Badge>
            <span className="text-muted-foreground">overwrites</span>
            <Badge variant="accent" className="font-mono">→ Ceiling wins</Badge>
            <span className="ml-auto text-muted-foreground">later rule = higher priority</span>
          </div>
        </Card>

        {/* ---- live rules ----------------------------------------------- */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Layers className="h-[18px] w-[18px] text-muted-foreground" />
            <h2 className="text-[15px] font-semibold tracking-tight">Live category rules</h2>
            {group && (
              <Badge variant="default" className="font-mono">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: group.color || '#2563eb' }} />
                {group.name}
              </Badge>
            )}
            {rules.length > 0 && (
              <span className="font-mono text-[11px] text-muted-foreground tnum">{rules.length} rule{rules.length === 1 ? '' : 's'}</span>
            )}
            {group && (distLoading || total > 0) && (
              <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground tnum">
                <BarChart3 className="h-3.5 w-3.5" />
                {distLoading
                  ? 'counting products…'
                  : <><span className="font-semibold text-foreground">{fmtInt(total)}</span> products categorised</>}
              </span>
            )}
          </div>

          {/* priority reminder */}
          <p className="mb-3 flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <ArrowDown className="h-3.5 w-3.5" />
            Read top to bottom — rules lower in the list take priority (last match wins).
          </p>

          {loading ? (
            <Card className="p-6 text-sm text-muted-foreground">Loading rules…</Card>
          ) : error ? (
            <Card className="flex items-center gap-2 p-6 text-sm text-rose-600">
              <AlertCircle className="h-4 w-4" /> {error}
            </Card>
          ) : !group || rules.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 p-12 text-center">
              <Tag className="h-7 w-7 text-muted-foreground" />
              <p className="font-medium">No category rules found</p>
              <p className="max-w-md text-[13px] text-muted-foreground">
                No master rule group currently sets <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{CATEGORY_FIELD}</code>.
                Add rules in <a href="/master-rules" className="text-primary hover:underline">Master rules</a>.
              </p>
            </Card>
          ) : (
            <Card>
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-right">#</TableHead>
                    <TableHead className="w-[15%]">Rule</TableHead>
                    <TableHead>Matches collection(s)</TableHead>
                    <TableHead className="w-[26%]">Sets category</TableHead>
                    <TableHead className="w-32 whitespace-nowrap text-right">Final volume</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule, i) => {
                    const collections = matchedCollections(rule);
                    return (
                      <TableRow key={rule.id ?? i} className={rule.enabled === false ? 'opacity-50' : ''}>
                        <TableCell className="text-right font-mono text-[12px] text-muted-foreground tnum align-top">{i + 1}</TableCell>
                        <TableCell className="align-top break-words">
                          <span className="font-medium">{rule.name || `Rule ${i + 1}`}</span>
                          {rule.enabled === false && (
                            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">off</span>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          {collections.length === 0 ? (
                            <span className="text-[12px] text-muted-foreground">any product</span>
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {collections.map((c, j) => (
                                <Badge key={j} variant="secondary" className="max-w-full whitespace-normal break-words text-left font-mono">{c}</Badge>
                              ))}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="align-top break-words">
                          <CategoryPath path={categoryPath(rule)} />
                        </TableCell>
                        <TableCell className="align-top text-right">
                          {distLoading && !dist ? (
                            <span className="inline-block h-3 w-12 animate-pulse rounded bg-muted align-middle" />
                          ) : (() => {
                            const c = ruleCount(rule);
                            if (c == null) return <span className="font-mono text-[12px] text-muted-foreground/60">—</span>;
                            if (c === 0) return <span className="font-mono text-[12px] text-muted-foreground/60 tnum">0 · 0%</span>;
                            return (
                              <span className="whitespace-nowrap font-mono text-[12px] tnum">
                                <span className="font-medium text-foreground">{fmtInt(c)}</span>
                                <span className="px-1 text-muted-foreground/50">·</span>
                                <span className="text-primary">{fmtPct(c, total)}</span>
                              </span>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* footer note + jump to editor */}
          {group && rules.length > 0 && (
            <p className="mt-3 text-[12px] text-muted-foreground">
              These rules live in the <span className="font-medium text-foreground">{group.name}</span> master rule group.
              Edit them in{' '}
              <a href="/master-rules" className="inline-flex items-center gap-1 text-primary hover:underline">
                Master rules <ExternalLink className="h-3 w-3" />
              </a>.
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
